import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Send, Sparkles, Lock, Heart, 
  MessageCircle, ChevronRight,
  Crown, Eye, Wifi, WifiOff, RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocketContext, WS_STATE } from "@/contexts/WebSocketContext";
import { MessageStatus, MESSAGE_STATUS, getMessageStatus, getStatusText } from "@/components/MessageStatus";
import { ReactionPicker, ReactionDisplay, ReactionButton, FloatingReaction, REACTIONS } from "@/components/MessageReactions";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const PREMIUM_TIERS = ["recovery_plus", "recovery_elite"];

const GuidedChatPage = ({ matchId: propMatchId, embedded = false }) => {
  const { matchId: paramMatchId } = useParams();
  const matchId = propMatchId || paramMatchId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    isConnected, 
    connectionState, 
    onMessage, 
    sendMessage,
    sendTypingIndicator,
    clearUnread 
  } = useWebSocketContext();
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  const [matchUser, setMatchUser] = useState(null);
  const [chatStatus, setChatStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [freeformText, setFreeformText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showPromptPicker, setShowPromptPicker] = useState(false);
  
  // Typing indicator state
  const [isTyping, setIsTyping] = useState(false);
  
  // Reactions state
  const [messageReactions, setMessageReactions] = useState({});
  const [activeReactionPicker, setActiveReactionPicker] = useState(null);
  const [floatingReaction, setFloatingReaction] = useState(null);
  
  const isPremium = PREMIUM_TIERS.includes(user?.subscription_tier);

  useEffect(() => {
    fetchChatData();
  }, [matchId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // WebSocket real-time message handling
  useEffect(() => {
    if (!isConnected || !matchId) return;
    
    // Listen for new messages
    const unsubMessage = onMessage('message', (data) => {
      const msg = data.message;
      if (msg && msg.match_id === matchId) {
        setMessages(prev => {
          // Avoid duplicates - check both id and temp_id
          if (prev.some(m => m.id === msg.id)) return prev;
          // Check if this is our own message being confirmed
          const tempMsg = prev.find(m => m.temp_id && m.sender_id === msg.sender_id && !m.id);
          if (tempMsg) {
            // Replace temp message with confirmed one
            return prev.map(m => 
              m.temp_id === tempMsg.temp_id 
                ? { ...msg, status: MESSAGE_STATUS.SENT, _statusAnimated: true }
                : m
            );
          }
          return [...prev, { ...msg, status: MESSAGE_STATUS.DELIVERED }];
        });
        // Mark as read since we're viewing this chat
        clearUnread(matchId);
      }
    });
    
    // Listen for typing indicators
    const unsubTyping = onMessage('typing', (data) => {
      if (data.match_id === matchId && data.user_id !== user?.id) {
        setIsTyping(true);
        // Auto-clear typing after 3 seconds
        setTimeout(() => setIsTyping(false), 3000);
      }
    });
    
    // Listen for read receipts - update status to READ
    const unsubRead = onMessage('read', (data) => {
      if (data.match_id === matchId) {
        setMessages(prev => prev.map(msg => 
          msg.sender_id === user?.id 
            ? { 
                ...msg, 
                read: true, 
                read_at: data.read_at || new Date().toISOString(),
                status: MESSAGE_STATUS.READ,
                _statusAnimated: false 
              }
            : msg
        ));
      }
    });

    // Listen for delivery confirmations
    const unsubDelivered = onMessage('delivered', (data) => {
      if (data.match_id === matchId) {
        setMessages(prev => prev.map(msg => 
          msg.sender_id === user?.id && msg.id === data.message_id
            ? { ...msg, delivered: true, status: MESSAGE_STATUS.DELIVERED, _statusAnimated: false }
            : msg
        ));
      }
    });

    // Listen for reactions
    const unsubReaction = onMessage('reaction', (data) => {
      if (data.match_id === matchId) {
        setMessageReactions(prev => {
          const msgReactions = [...(prev[data.message_id] || [])];
          // Remove existing reaction by same user
          const filtered = msgReactions.filter(r => r.user_id !== data.user_id);
          // Add new reaction
          filtered.push({
            message_id: data.message_id,
            user_id: data.user_id,
            reaction: data.reaction,
            created_at: new Date().toISOString()
          });
          return { ...prev, [data.message_id]: filtered };
        });
        
        // Show floating reaction animation if from other user
        if (data.user_id !== user?.id) {
          const reactionObj = REACTIONS.find(r => r.name === data.reaction);
          if (reactionObj) {
            setFloatingReaction({ emoji: reactionObj.emoji, messageId: data.message_id });
          }
        }
      }
    });

    // Listen for reaction removals
    const unsubReactionRemoved = onMessage('reaction_removed', (data) => {
      if (data.match_id === matchId) {
        setMessageReactions(prev => {
          const msgReactions = prev[data.message_id] || [];
          return { 
            ...prev, 
            [data.message_id]: msgReactions.filter(r => r.user_id !== data.user_id) 
          };
        });
      }
    });
    
    return () => {
      unsubMessage();
      unsubTyping();
      unsubRead();
      unsubDelivered();
      unsubReaction();
      unsubReactionRemoved();
    };
  }, [isConnected, matchId, onMessage, clearUnread, user?.id]);

  // Mark messages as read once per opened chat (via REST fallback)
  const markedReadRef = useRef(null);
  useEffect(() => {
    if (matchId && messages.length > 0 && markedReadRef.current !== matchId) {
      markedReadRef.current = matchId;
      api.markMessagesAsRead(matchId).catch(() => {});
      clearUnread(matchId);
    }
  }, [matchId, messages.length, clearUnread]);

  // Handle typing indicator via WebSocket
  const handleTyping = useCallback(() => {
    if (!matchId) return;
    
    // Send via WebSocket if connected
    if (isConnected) {
      sendTypingIndicator(matchId);
    } else {
      // Fallback to REST
      api.setTypingStatus(matchId).catch(() => {});
    }
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Debounce typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      if (isConnected) {
        sendTypingIndicator(matchId);
      }
    }, 3000);
  }, [matchId, isConnected, sendTypingIndicator]);

  const fetchChatData = async () => {
    try {
      setLoading(true);
      
      // Fetch chat status and messages
      const statusRes = await api.getGuidedChatStatus(matchId);
      setChatStatus(statusRes.data);
      setMessages(statusRes.data.messages || []);
      setPrompts(statusRes.data.suggested_prompts || []);
      
      // Fetch match user details
      const matchRes = await api.getMatchDetail(matchId);
      setMatchUser(matchRes.data);
    } catch (error) {
      console.error("Failed to load chat:", error);
      toast.error("Failed to load conversation");
      navigate("/messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSendGuidedMessage = async () => {
    if (!selectedPrompt || !answerText.trim() || sending) return;
    
    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      temp_id: tempId,
      id: null,
      sender_id: user.id,
      message_type: 'guided',
      prompt_text: selectedPrompt.prompt,
      answer: answerText.trim(),
      created_at: new Date().toISOString(),
      sending: true,
      status: MESSAGE_STATUS.SENDING,
    };
    
    // Optimistic update - show message immediately
    setMessages(prev => [...prev, tempMessage]);
    setSelectedPrompt(null);
    setAnswerText("");
    setShowPromptPicker(false);
    setSending(true);
    
    try {
      const response = await api.sendGuidedMessage(matchId, {
        prompt_id: `prompt_${Date.now()}`,
        prompt_text: tempMessage.prompt_text,
        answer: tempMessage.answer
      });
      
      // Replace temp message with real one
      setMessages(prev => prev.map(msg => 
        msg.temp_id === tempId 
          ? { ...response.data.message, status: MESSAGE_STATUS.SENT, _statusAnimated: false }
          : msg
      ));
      setChatStatus(response.data.status);
      
      if (response.data.unlocked_now) {
        toast.success("🎉 Free chat unlocked! You can now send any message.");
      }
      
      // Refresh prompts
      const statusRes = await api.getGuidedChatStatus(matchId);
      setPrompts(statusRes.data.suggested_prompts || []);
    } catch (error) {
      // Mark message as failed
      setMessages(prev => prev.map(msg => 
        msg.temp_id === tempId 
          ? { ...msg, sending: false, failed: true, status: MESSAGE_STATUS.FAILED }
          : msg
      ));
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleSendFreeformMessage = async () => {
    if (!freeformText.trim() || sending) return;
    
    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      temp_id: tempId,
      id: null,
      sender_id: user.id,
      content: freeformText.trim(),
      created_at: new Date().toISOString(),
      sending: true,
      status: MESSAGE_STATUS.SENDING,
    };
    
    // Optimistic update
    setMessages(prev => [...prev, tempMessage]);
    setFreeformText("");
    setSending(true);
    
    try {
      const response = await api.sendFreeformMessage(matchId, {
        content: tempMessage.content
      });
      
      // Replace temp message with real one
      setMessages(prev => prev.map(msg => 
        msg.temp_id === tempId 
          ? { ...response.data.message, status: MESSAGE_STATUS.SENT, _statusAnimated: false }
          : msg
      ));
    } catch (error) {
      // Mark message as failed
      setMessages(prev => prev.map(msg => 
        msg.temp_id === tempId 
          ? { ...msg, sending: false, failed: true, status: MESSAGE_STATUS.FAILED }
          : msg
      ));
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Retry failed message
  const handleRetryMessage = async (failedMsg) => {
    // Mark as sending again
    setMessages(prev => prev.map(msg => 
      (msg.temp_id === failedMsg.temp_id || msg.id === failedMsg.id)
        ? { ...msg, sending: true, failed: false, status: MESSAGE_STATUS.SENDING }
        : msg
    ));
    
    try {
      let response;
      if (failedMsg.message_type === 'guided') {
        response = await api.sendGuidedMessage(matchId, {
          prompt_id: `prompt_${Date.now()}`,
          prompt_text: failedMsg.prompt_text,
          answer: failedMsg.answer
        });
      } else {
        response = await api.sendFreeformMessage(matchId, {
          content: failedMsg.content || failedMsg.answer
        });
      }
      
      // Replace with real message
      setMessages(prev => prev.map(msg => 
        (msg.temp_id === failedMsg.temp_id || msg.id === failedMsg.id)
          ? { ...response.data.message, status: MESSAGE_STATUS.SENT }
          : msg
      ));
    } catch (error) {
      // Mark as failed again
      setMessages(prev => prev.map(msg => 
        (msg.temp_id === failedMsg.temp_id || msg.id === failedMsg.id)
          ? { ...msg, sending: false, failed: true, status: MESSAGE_STATUS.FAILED }
          : msg
      ));
      toast.error("Failed to resend message");
    }
  };

  // Handle reaction on a message
  const handleReaction = useCallback(async (messageId, reaction) => {
    // Optimistic update
    setMessageReactions(prev => {
      const msgReactions = [...(prev[messageId] || [])];
      const existingIdx = msgReactions.findIndex(r => r.user_id === user.id);
      
      if (existingIdx >= 0) {
        if (msgReactions[existingIdx].reaction === reaction.name) {
          // Same reaction - remove it
          msgReactions.splice(existingIdx, 1);
        } else {
          // Different reaction - replace it
          msgReactions[existingIdx] = {
            ...msgReactions[existingIdx],
            reaction: reaction.name
          };
        }
      } else {
        // Add new reaction
        msgReactions.push({
          message_id: messageId,
          user_id: user.id,
          reaction: reaction.name,
          created_at: new Date().toISOString()
        });
      }
      
      return { ...prev, [messageId]: msgReactions };
    });
    
    // Close picker
    setActiveReactionPicker(null);
    
    // Send via WebSocket if connected, otherwise REST
    if (isConnected) {
      sendMessage({
        type: 'reaction',
        message_id: messageId,
        match_id: matchId,
        reaction: reaction.name
      });
    } else {
      try {
        await api.addReaction(messageId, { reaction: reaction.name });
      } catch (error) {
        // Revert on error
        toast.error("Failed to add reaction");
      }
    }
  }, [user?.id, isConnected, matchId, sendMessage]);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatReadTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className={`${embedded ? "h-full" : "min-h-screen"} bg-gray-100 flex items-center justify-center`}>
        <div className="w-12 h-12 border-4 border-[#1C3A7A]/30 border-t-[#1C3A7A] rounded-full animate-spin"></div>
      </div>
    );
  }

  const isFreeChatUnlocked = chatStatus?.free_chat_unlocked;
  const myProgress = chatStatus?.my_progress || 0;
  const theirProgress = chatStatus?.their_progress || 0;
  const unlockThreshold = chatStatus?.unlock_threshold || 3;

  return (
    <div className={`${embedded ? "h-full overflow-hidden" : "min-h-screen"} bg-gray-50 flex flex-col`} data-testid="guided-chat-page">
      {/* Connection Status Banner */}
      {connectionState === WS_STATE.RECONNECTING && (
        <div className="bg-amber-500 text-white text-center py-1 px-4 text-sm flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          Reconnecting...
        </div>
      )}
      {connectionState === WS_STATE.ERROR && (
        <div className="bg-red-500 text-white text-center py-1 px-4 text-sm flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          Connection lost. Messages may be delayed.
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center gap-3 shadow-sm">
        {!embedded && (
          <button onClick={() => navigate("/messages")} className="p-1" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6 text-[#1C3A7A]" />
          </button>
        )}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
              {matchUser?.photos?.[0] ? (
                <img src={matchUser.photos[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xl font-bold">
                  {matchUser?.name?.charAt(0) || "?"}
                </div>
              )}
            </div>
            {/* Online indicator */}
            {isConnected && (
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-[#1C3A7A]">{matchUser?.name}</h2>
            <p className="text-xs text-gray-500">
              {matchUser?.compatibility_score}% Compatible
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar (only show if not unlocked) */}
      {!isFreeChatUnlocked && (
        <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] px-4 py-3 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span className="text-sm font-medium">Unlock Free Chat</span>
            </div>
            <span className="text-xs opacity-80">
              {myProgress + theirProgress}/{unlockThreshold * 2} messages
            </span>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <div className="text-xs mb-1 opacity-80">You: {myProgress}/{unlockThreshold}</div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#FAB710] rounded-full transition-all"
                  style={{ width: `${(myProgress / unlockThreshold) * 100}%` }}
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs mb-1 opacity-80">{matchUser?.name}: {theirProgress}/{unlockThreshold}</div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-400 rounded-full transition-all"
                  style={{ width: `${(theirProgress / unlockThreshold) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-[#1C3A7A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-10 h-10 text-[#1C3A7A]" />
            </div>
            <h3 className="font-semibold text-[#1C3A7A] mb-2">Start a Meaningful Conversation</h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              Use conversation prompts to get to know {matchUser?.name}. 
              After 3 exchanges each, free chat unlocks!
            </p>
          </div>
        )}

        {messages.map((msg) => {
          const isOwn = msg.sender_id === user.id;
          const isGuided = msg.message_type === "guided";
          const messageStatus = getMessageStatus(msg, isOwn);
          const msgId = msg.id || msg.temp_id;
          const msgReactions = messageReactions[msg.id] || [];
          const showReactionPicker = activeReactionPicker === msgId;
          
          return (
            <motion.div
              key={msgId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${isOwn ? "justify-end" : "justify-start"} group`}
            >
              <div className={`max-w-[80%] relative ${isOwn ? "items-end" : "items-start"}`}>
                {/* Floating reaction animation */}
                <AnimatePresence>
                  {floatingReaction?.messageId === msg.id && (
                    <FloatingReaction 
                      emoji={floatingReaction.emoji} 
                      onComplete={() => setFloatingReaction(null)}
                    />
                  )}
                </AnimatePresence>
                
                {isGuided && (
                  <div className={`text-xs px-2 py-1 rounded-t-lg ${
                    isOwn ? "bg-[#FAB710]/20 text-[#FAB710]" : "bg-[#1C3A7A]/10 text-[#1C3A7A]"
                  }`}>
                    {msg.prompt_text}
                  </div>
                )}
                
                {/* Message bubble with reaction button */}
                <div className="relative">
                  <div className={`px-4 py-3 ${
                    isGuided 
                      ? isOwn 
                        ? "bg-[#FAB710] text-white rounded-b-2xl rounded-tl-2xl"
                        : "bg-white border-2 border-[#1C3A7A]/20 text-gray-800 rounded-b-2xl rounded-tr-2xl"
                      : isOwn
                        ? "bg-[#FAB710] text-white rounded-2xl rounded-br-md"
                        : "bg-[#1C3A7A] text-white rounded-2xl rounded-bl-md"
                  }`}>
                    <p className="break-words">{msg.answer || msg.content}</p>
                    <div className={`flex items-center justify-end gap-1.5 mt-1 ${
                      isOwn ? "text-white/70" : isGuided ? "text-gray-400" : "text-white/70"
                    }`}>
                      <span className="text-xs">{formatTime(msg.created_at)}</span>
                      {isOwn && messageStatus && (
                        <div className="flex items-center" title={getStatusText(messageStatus)}>
                          <MessageStatus 
                            status={messageStatus} 
                            size="sm"
                            lightMode={true}
                            showAnimation={!msg._statusAnimated}
                          />
                          {messageStatus === MESSAGE_STATUS.FAILED && (
                            <button 
                              onClick={() => handleRetryMessage(msg)}
                              className="ml-1 hover:text-white transition-colors"
                              title="Retry"
                            >
                              <RotateCcw className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Reaction button - shows on hover */}
                  {msg.id && (
                    <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity
                      ${isOwn ? '-left-8' : '-right-8'}`}>
                      <ReactionButton onClick={() => setActiveReactionPicker(showReactionPicker ? null : msgId)} />
                    </div>
                  )}
                  
                  {/* Reaction picker */}
                  <AnimatePresence>
                    {showReactionPicker && (
                      <ReactionPicker
                        position={isOwn ? 'top' : 'top'}
                        currentReaction={msgReactions.find(r => r.user_id === user.id)?.reaction}
                        onSelect={(reaction) => handleReaction(msg.id, reaction)}
                        onClose={() => setActiveReactionPicker(null)}
                      />
                    )}
                  </AnimatePresence>
                </div>
                
                {/* Reactions display */}
                {msgReactions.length > 0 && (
                  <ReactionDisplay 
                    reactions={msgReactions}
                    currentUserId={user.id}
                    isOwnMessage={isOwn}
                    onReactionClick={(reaction) => handleReaction(msg.id, reaction)}
                  />
                )}
                
                {/* Premium read receipt details */}
                {isOwn && isPremium && messageStatus === MESSAGE_STATUS.READ && msg.read_at && (
                  <motion.div 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-gray-400 text-right mt-0.5 flex items-center justify-end gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    <span>Read {formatReadTime(msg.read_at)}</span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          );
        })}
        
        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="bg-gray-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t px-4 py-3">
        {isFreeChatUnlocked ? (
          /* Free-form input */
          <form onSubmit={(e) => { e.preventDefault(); handleSendFreeformMessage(); }} className="flex gap-2">
            <input
              type="text"
              value={freeformText}
              onChange={(e) => { setFreeformText(e.target.value); handleTyping(); }}
              placeholder="Type a message..."
              className="flex-1 h-12 px-4 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-[#1C3A7A]/20"
              data-testid="freeform-input"
            />
            <button
              type="submit"
              disabled={!freeformText.trim() || sending}
              className="w-12 h-12 rounded-full bg-[#FAB710] hover:bg-[#e8850f] flex items-center justify-center disabled:opacity-50 transition-colors"
              data-testid="send-freeform-btn"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </form>
        ) : selectedPrompt ? (
          /* Answer a prompt */
          <div className="space-y-3">
            <div className="bg-[#1C3A7A]/5 rounded-xl p-3">
              <p className="text-sm text-[#1C3A7A] font-medium">{selectedPrompt.prompt}</p>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={answerText}
                onChange={(e) => { setAnswerText(e.target.value); handleTyping(); }}
                placeholder="Your answer..."
                className="flex-1 h-12 px-4 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-[#1C3A7A]/20"
                maxLength={500}
                data-testid="guided-answer-input"
              />
              <button
                onClick={handleSendGuidedMessage}
                disabled={!answerText.trim() || sending}
                className="w-12 h-12 rounded-full bg-[#FAB710] hover:bg-[#e8850f] flex items-center justify-center disabled:opacity-50 transition-colors"
                data-testid="send-guided-btn"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
            <button
              onClick={() => { setSelectedPrompt(null); setAnswerText(""); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Choose a different prompt
            </button>
          </div>
        ) : (
          /* Prompt picker */
          <div>
            <button
              onClick={() => setShowPromptPicker(true)}
              className="w-full h-12 px-4 bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] text-white rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              data-testid="choose-prompt-btn"
            >
              <Sparkles className="w-5 h-5" />
              Choose a conversation starter
            </button>
          </div>
        )}
      </div>

      {/* Prompt Picker Modal */}
      <AnimatePresence>
        {showPromptPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
            onClick={() => setShowPromptPicker(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="bg-white w-full max-w-lg max-h-[70vh] rounded-t-3xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="prompt-picker-modal"
            >
              <div className="p-4 border-b bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] text-white">
                <h3 className="text-lg font-bold">Choose a Conversation Starter</h3>
                <p className="text-sm opacity-80">
                  Pick a prompt to start a meaningful conversation
                </p>
              </div>
              
              <div className="overflow-y-auto max-h-[50vh] p-4 space-y-3">
                {prompts.map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setShowPromptPicker(false);
                    }}
                    className="w-full text-left p-4 bg-gray-50 hover:bg-[#1C3A7A]/5 rounded-xl transition-colors border-2 border-transparent hover:border-[#1C3A7A]/20"
                    data-testid={`prompt-option-${idx}`}
                  >
                    <span className="text-xs text-[#FAB710] font-medium block mb-1">
                      {prompt.category_title}
                    </span>
                    <p className="text-gray-800">{prompt.prompt}</p>
                  </button>
                ))}
                
                {prompts.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No more prompts available. You&apos;ve used them all!
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Celebration Modal when chat unlocks */}
      <AnimatePresence>
        {chatStatus?.free_chat_unlocked && messages.length > 0 && messages[messages.length - 1]?.unlocked_now && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-3xl p-8 text-center max-w-sm">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Free Chat Unlocked!
              </h2>
              <p className="text-gray-500 mb-6">
                You and {matchUser?.name} have shown genuine interest. 
                You can now send any message!
              </p>
              <Button
                onClick={() => {}}
                className="w-full bg-[#1C3A7A] hover:bg-[#15305f]"
              >
                Continue Chatting
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuidedChatPage;
